import React from 'react';
import { Grid, TextField, Button, Box, Typography, Autocomplete, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { unableConsentReasons, doctorGroups } from '../constants/options';

const SignatureField = ({ label, name: fieldName, value, nameValue, dateValue, onSign, onNameChange, signName, isDoctor = false, onChange }) => (
  <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, height: '100%', backgroundColor: '#fff' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#008080' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isDoctor ? (
        <FormControl fullWidth size="small">
          <InputLabel>{`Name (${label})`}</InputLabel>
          <Select
            name={signName}
            value={nameValue || ''}
            onChange={onChange}
            label={`Name (${label})`}
          >
            <MenuItem value=""><em>Select Doctor</em></MenuItem>
            {doctorGroups["AnaesthesiaSpecialists"]?.map((doctor, idx) => (
              <MenuItem key={idx} value={doctor}>{doctor}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        onNameChange && (
          <TextField
            label="Full Name"
            name={signName}
            value={nameValue || ''}
            onChange={onNameChange}
            size="small"
            fullWidth
          />
        )
      )}
      <Button
        variant="outlined"
        onClick={() => onSign(fieldName)}
        sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none' }}
      >
        {value ? 'Update Signature' : 'Provide Signature'}
      </Button>
      {value && (
        <Box sx={{ border: '1px solid #ccc', p: 1, borderRadius: 1, textAlign: 'center' }}>
          <img
            src={`data:image/png;base64,${value}`}
            alt={`${label} Signature`}
            style={{ maxHeight: '60px', maxWidth: '100%' }}
          />
        </Box>
      )}
      <TextField
        label="Date & Time"
        type="datetime-local"
        value={dateValue || ''}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        InputProps={{ readOnly: true }}
      />
    </Box>
  </Box>
);

const SurrogateSection = ({ formData, handleChange, handleNameChange, handleSignatureOpen, setFormData }) => {
  return (
    <Box sx={{ mt: 6, pt: 4, borderTop: '3px solid #008080' }}>
      <Box sx={{ border: '1px solid #aae9e9ff', p: 3, borderRadius: 2, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center', color: '#008080' }}>
          CONSENT OF PATIENT REPRESENTATIVE / SURROGATE / Children below 18 yrs / vulnerable patients
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="body2">The patient is unable to consent because:</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              freeSolo
              options={unableConsentReasons}
              value={formData.unable_consent_reason || ''}
              onChange={(e, val) => setFormData(prev => ({ ...prev, unable_consent_reason: val }))}
              renderInput={(params) => <TextField {...params} placeholder="Reason" name="unable_consent_reason" onChange={handleChange} variant="outlined" size="small" />}
            />
          </Grid>
          <Grid item xs={1}><Typography variant="body2">And I,</Typography></Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              placeholder="Attender Name"
              name="attender_name"
              value={formData.attender_name || ''}
              onChange={handleNameChange}
              fullWidth
              size="small"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 1 }}>
              (name/relationship to the patient) therefore consent for the procedure with the doctor's designee and hereby consent to this procedure.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Patient Rep"
            name="patient_rep_signature"
            value={formData.patient_rep_signature}
            nameValue={formData.patient_rep_patient_name}
            dateValue={formData.patient_rep_signature_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="patient_rep_patient_name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Witness"
            name="witness_signature_unable_consent"
            value={formData.witness_signature_unable_consent}
            nameValue={formData.witness_name_unable_consent}
            dateValue={formData.witness_signature_unable_consent_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="witness_name_unable_consent"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Doctor/Anaesthetist"
            name="doctor_signature_unable_consent"
            value={formData.doctor_signature_unable_consent}
            nameValue={formData.doctor_signature_unable_consent_patient_name}
            dateValue={formData.doctor_signature_unable_consent_date_time}
            onSign={handleSignatureOpen}
            signName="doctor_signature_unable_consent_patient_name"
            isDoctor={true}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SignatureField
            label="Interpreter"
            name="interpreter_signature_unable_consent"
            value={formData.interpreter_signature_unable_consent}
            nameValue={formData.interpreter_name_unable_consent}
            dateValue={formData.interpreter_signature_unable_consent_date_time}
            onSign={handleSignatureOpen}
            onNameChange={handleNameChange}
            signName="interpreter_name_unable_consent"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurrogateSection;
